import qs from 'qs';
import { useLocation } from 'react-router-dom';
import Copy from 'src/component/Copy';
import InnerHeader from 'src/component/InnerHeader';
import NoxTable from 'src/component/Table';
import { useDebounce } from 'src/hooks/useDebounce';
import {
  useGetMaintenance,
  fetchMaintenanceCSVData
} from 'src/reactQuery/payments/maintenance/queries/useGetMaintenance';
import { MaintenanceRow } from 'src/services/napi/maintenance/interfaces/maintenance.interface';
import { MappedMaintenance } from './interfaces/mappedMaintenance.interface';

const Maintenance = () => {
  const urlLocation = useLocation();

  const params = qs.parse(urlLocation.search, { ignoreQueryPrefix: true });

  const { data: maintenanceData, isLoading, refetch: refetchSearch } = useGetMaintenance({
    orderBy: params.orderBy?.toString(),
    orderDirection: params.orderDirection?.toString(),
    page: params.page?.toString(),
    pageSize: params.pageSize?.toString(),
    search: params.search?.toString()
  });

  useDebounce(() => refetchSearch(), 500, [params.search]);

  const onDownloadCsv = async () => {
    const response = await fetchMaintenanceCSVData();

    if (!response) return;

    const url = window.URL.createObjectURL(response);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `maintenance.csv`);
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const mapToTable = (rows: MaintenanceRow[] | undefined) => {
    const data: MappedMaintenance[] = [];

    if (!rows || rows.length === 0) {
      return data;
    }

    rows.forEach(
      (
        {
          _id,
          apartment,
          contact,
          name,
          paid,
          pid,
          status,
          total,
          totalPaidDifference,
          totalPaidShouldBe
        },
        index
      ) => {
        data.push({
          _id,
          apartment,
          contact,
          key: index,
          name,
          paid,
          pid,
          status,
          total,
          totalPaidDifference,
          totalPaidShouldBe
        });
      }
    );

    return data;
  };

  const filteredData = mapToTable(maintenanceData?.maintenance);

  const columns = [
    {
      dataIndex: 'name',
      key: 'deal_name',
      render: (name: string) => {
        return (
          <div
            style={{
              color: 'black',
              fontWeight: 700
            }}
          >
            <Copy text={name} />
          </div>
        );
      },
      sorter: true,
      title: 'Name'
    },
    {
      dataIndex: 'contact',
      key: 'contact',
      render: (contact: string) => {
        return <Copy text={contact} />;
      },
      sorter: true,
      title: 'Contact'
    },
    {
      dataIndex: 'pid',
      key: 'user_pid',
      render: (pid: string) => {
        return <Copy text={pid} />;
      },
      sorter: true,
      title: 'Pid'
    },
    {
      dataIndex: 'total',
      key: 'total',
      render: (total: number) => {
        return (
          <Copy text={total} title="Total maintenance the resident should have paid to date" />
        );
      },
      sorter: true,
      title: 'Total'
    },
    {
      dataIndex: 'totalPaidShouldBe',
      key: 'shouldBe',
      render: (totalPaidShouldBe: number) => {
        return (
          <Copy text={totalPaidShouldBe} title="Amount the resident should have paid as of today" />
        );
      },
      sorter: true,
      title: 'Should be'
    },
    {
      dataIndex: 'paid',
      key: 'paid',
      render: (paid: number) => {
        return <Copy text={paid} title="Amount the resident has paid as of today" />;
      },
      sorter: true,
      title: 'Paid'
    },
    {
      dataIndex: 'totalPaidDifference',
      key: 'debt',
      render: (totalPaidDifference: number) => {
        return (
          <Copy
            text={totalPaidDifference}
            title="Difference (Should be − Paid). Positive = owes, negative = overpaid"
          />
        );
      },
      sorter: true,
      title: 'Difference'
    }
  ];

  const actions = [
    {
      onClick: onDownloadCsv,
      title: 'Download CSV',
      type: 'primary'
    }
  ];

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={maintenanceData?.paging.total} title="Maintenance" />
      {/* @ts-ignore */}
      <NoxTable
        actions={actions}
        checkbox={false}
        columns={columns}
        data={filteredData}
        dragAndDropUrl="content"
        loading={isLoading}
        totalPages={maintenanceData?.paging.total}
      />
    </div>
  );
};

export default Maintenance;
